import { type JSX, useRef } from 'react';

import { type CategoryGroup } from '@cx-modules/constants';
import { useTranslation } from '@cx-modules/i18n-provider';
import { Arrow } from '@cx-modules/ui';
import { mergePersistentParams } from '@cx-modules/utils';

import { useOutrightsCouponContext } from '../../../../hooks';
import { type LoadOutrightsCouponArgs } from '../../../../types';
import { type MarketBet } from '../../../_common';
import { useShowMore } from '../../hooks';
import { BetOddButton } from '../BetOddButton';

import {
  BetItem,
  BetName,
  BetsShowMore,
  CompactBetsGridContainer,
  CompactBetsGridWrapper,
  OddButtonContainer,
  TableHeaderText,
  TableHeaderWrapper,
} from './style';

const arrowColor = 'var(--oc-primary-blue)';

export interface CompactBetsGridProps {
  bets: MarketBet[];
  categoryGroup?: CategoryGroup;
  marketId: number;
  marketName: string;
  marketUrl?: string;
  newUI?: boolean;
  oddsShown: LoadOutrightsCouponArgs['oddsShown'];
  requestUrl: string;
  subEventName?: string;
}

export const CompactBetsGrid = ({
  bets,
  categoryGroup,
  marketName,
  marketUrl,
  newUI,
  oddsShown,
  requestUrl,
  subEventName,
}: CompactBetsGridProps): JSX.Element => {
  const categoryGroupNs = categoryGroup?.translationFile;

  const { t } = useTranslation(categoryGroupNs ? [categoryGroupNs, 'label'] : ['label']);

  const gridRef = useRef<HTMLDivElement | null>(null);

  const { handleShowMoreClick, hideShowMoreSection, showMore, showMoreText, visibleBets } = useShowMore({
    bets,
    gridRef,
    maxMobileBets: 4,
    maxTabletBets: 6,
  });

  const translateBetNames = useOutrightsCouponContext(state => state.translateBetNames);

  return (
    <CompactBetsGridWrapper ref={gridRef}>
      <TableHeaderWrapper>
        <TableHeaderText>Best Odds</TableHeaderText>
      </TableHeaderWrapper>
      <CompactBetsGridContainer className={newUI ? 'newUI' : ''}>
        {visibleBets.map(bet => {
          const isOverOrUnder = ['Over', 'Under'].includes(bet.betName);
          const line = bet?.line?.absolute;
          const betName = isOverOrUnder ? `${bet.betName} ${line || ''}`.trim() : bet.betName.trim();
          const betNameUrl = marketUrl
            ? mergePersistentParams(
                `${marketUrl}?selectionName=${bet.betName.toLowerCase().replace(/[ ]/g, '-')}`,
                requestUrl,
              )
            : undefined;

          return (
            <BetItem className={newUI ? 'newUI' : ''} key={`${bet.betId}-${bet.betName}`}>
              <BetName className={marketUrl ? '' : 'disabled'} href={betNameUrl}>
                {translateBetNames ? t(betName) : betName}
              </BetName>
              <OddButtonContainer>
                <BetOddButton
                  bet={bet}
                  marketName={marketName}
                  newUI={newUI}
                  oddsShown={oddsShown}
                  subEventName={subEventName}
                />
              </OddButtonContainer>
            </BetItem>
          );
        })}
      </CompactBetsGridContainer>
      {hideShowMoreSection && (
        <BetsShowMore
          data-track={`show-more-outrights-button-${marketName.replace(' ', '-')}`}
          onClick={handleShowMoreClick}
        >
          {showMoreText}
          {showMore ? <Arrow colour={arrowColor} up x={7} y={0} /> : <Arrow colour={arrowColor} down x={7} y={-3} />}
        </BetsShowMore>
      )}
    </CompactBetsGridWrapper>
  );
};
